import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconDownload, IconEdit, IconTrash, IconView } from '../../components/icons';
import Pagination from '../../components/Pagination';
import { useAuth } from '../../auth/useAuth';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';
import { downloadListCsv } from '../../utils/csv';
import { roleLabel } from '../../utils/roles';
import usePaginatedList from '../../hooks/usePaginatedList';
import useCsvExport from '../../hooks/useCsvExport';

// TS port of the source (Falah) app's pages/system/Users.jsx. Deletion here
// is a real DELETE (UserController::destroy on the backend) rather than an
// activate/deactivate toggle — VNPF's API has no such toggle endpoint;
// "Active"/"Inactive" is instead an editable field on the user record
// itself (see UserForm's Status select), and a user with financial records
// simply can't be deleted (the backend returns a 422 explaining why).

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

interface UserRow {
  id: number;
  formatted_id: string;
  full_name: string;
  email: string;
  status: 'active' | 'inactive';
  roles: string[];
}

export function Users() {
  const { user: currentUser } = useAuth();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const filters = useMemo(
    () => ({ search: search || undefined, status: status || undefined }),
    [search, status]
  );
  const { data, loading, error, setPage, reload: load } = usePaginatedList<UserRow>('/users', filters);

  const remove = async (u: UserRow) => {
    if (!(await confirmDelete(`Delete account for ${u.full_name}?`))) return;
    try {
      await api.delete(`/users/${u.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const { exporting, exportCsv } = useCsvExport(() =>
    downloadListCsv<UserRow>(
      api,
      '/users',
      { search: search || undefined, status: status || undefined },
      [
        { label: 'ID', value: (r) => r.formatted_id },
        { label: 'Name', value: (r) => r.full_name },
        { label: 'Email', value: (r) => r.email },
        { label: 'Status', value: (r) => (r.status === 'active' ? 'Active' : 'Inactive') },
        { label: 'Roles', value: (r) => r.roles.map(roleLabel).join('; ') },
      ],
      'users.csv'
    )
  );

  return (
    <>
      <header className="page-header">
        <h1>Users</h1>
        <div className="page-header-actions">
          <IconButton
            icon={IconDownload}
            label={exporting ? 'Exporting…' : 'Download CSV'}
            onClick={exportCsv}
            disabled={exporting}
          />
          <Link className="btn btn-primary" to="/users/new">
            + Add user
          </Link>
        </div>
      </header>
      <p className="muted">Accounts that can sign in to VNPF One.</p>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={5} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((u) => (
                  <tr key={u.id}>
                    <td className="muted">{u.formatted_id}</td>
                    <td>
                      <strong>{u.full_name}</strong>
                    </td>
                    <td>{u.email}</td>
                    <td>
                      <span className={`badge badge-${u.status === 'active' ? 'active' : 'inactive'}`}>
                        {u.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/users/${u.id}`} />
                      <IconButton icon={IconEdit} label="Edit" to={`/users/${u.id}/edit`} />
                      <IconButton
                        icon={IconTrash}
                        label="Delete"
                        variant="danger-ghost"
                        onClick={() => remove(u)}
                        hidden={u.id === currentUser?.id}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="muted center">
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
